// passFinder.ts — Client wrapper for /api/route/pass-finder.
//
// Calls the mountain-pass auto-router and returns a candidate flight
// plan whose route avoids terrain at the requested cruise altitude.
//
// Usage:
//   const result = await findTerrainClearanceRoute({
//     origin:      { ident: 'KMFR', lat: 42.3742, lon: -122.873 },
//     destination: { ident: 'KSIY', lat: 41.7813, lon: -122.464 },
//     cruiseAltFt: 7500,
//     clearanceFt: 500,
//     avoidTfrs: false,
//   });
//   if (result?.ok) {
//     // result.waypoints is a sparse list of 3–6 waypoints
//     // result.diagnostics describes the routing decision
//   }

export interface PassFinderWaypoint {
  ident?: string;
  lat: number;
  lon: number;
}

export interface PassFinderRequest {
  origin: PassFinderWaypoint;
  destination: PassFinderWaypoint;
  cruiseAltFt: number;
  /** Hard clearance floor below cruise (default 500). */
  clearanceFt?: number;
  /** Corridor half-width in NM (default 5 → 10 NM total). */
  corridorHalfNm?: number;
  /** Avoid active TFRs (default false). */
  avoidTfrs?: boolean;
  /** Avoid prohibited airspace (default true). */
  avoidProhibited?: boolean;
  /** Abort signal for the network request. */
  signal?: AbortSignal;
}

export interface PassFinderResultWaypoint {
  ident?: string;
  lat: number;
  lon: number;
  note?: 'origin' | 'destination' | 'pass' | 'detour';
}

export interface PassFinderDiagnostics {
  directDistanceNm: number;
  routedDistanceNm: number;
  directMaxTerrainFt: number;
  routedMaxTerrainFt: number;
  directMinAglFt: number;
  routedMinAglFt: number;
  passesUsed: string[];
  tfrsAvoided: string[];
  reason: 'success' | 'success-soft' | 'no-conflict' | 'no-solution';
  /** True when the strict pass returned no solution and we fell back
   *  to the soft pass; the returned route may overfly terrain higher
   *  than the cruise altitude. UI must warn loudly. */
  softFallback?: boolean;
}

export interface PassFinderResult {
  ok: true;
  waypoints: PassFinderResultWaypoint[];
  diagnostics: PassFinderDiagnostics;
}

export interface PassFinderError {
  ok: false;
  error: string;
}

export async function findTerrainClearanceRoute(
  req: PassFinderRequest,
): Promise<PassFinderResult | PassFinderError | null> {
  const { signal, ...body } = req;
  try {
    const r = await fetch('/api/route/pass-finder', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    });
    if (!r.ok) {
      const text = await r.text().catch(() => '');
      return { ok: false, error: `pass-finder ${r.status}: ${text || r.statusText}` };
    }
    return (await r.json()) as PassFinderResult;
  } catch (e) {
    if ((e as { name?: string })?.name === 'AbortError') return null;
    return { ok: false, error: (e as Error)?.message ?? String(e) };
  }
}
